/**
 * CF-GROWTH-V1 — pure client-side projection of the public growth endpoint.
 *
 * Mirrors the bonus-state.ts discipline: every contract decision lives here, and a FAILURE NEVER
 * renders a positive state. A malformed or unexpected body resolves to `hidden` — never a ticker
 * with half-parsed rows. No React, no window, no I/O: unit-testable in plain Node.
 *
 * CF-POT-COUNTER-REMOVE-V1 (PL TG #12916): this module also projected `GET /api/v1/bonus/pot` into a
 * `PotView` for the bonus-pool counter. That surface was removed from the app, so its type, sentinel
 * and parser were removed with it rather than left as untested dead code. The server route is
 * unchanged and still serves; it simply has no client. Restoring the counter is a revert of
 * `CF-SCREENS-GOLIVE-V1` (4db3ffa), not a rebuild.
 *
 * Server shape (locked, backend R2):
 *   GET /api/v1/ticker ->
 *     { contract_version:1, items:[{ display:string, won_ton:string, tier:string,
 *       ago_seconds:number }] }
 *     { error:"ticker_unavailable" }                                 (500)
 *
 * The server already floors TON to a display string (BigInt floor). The client NEVER re-derives
 * money: it renders the string verbatim.
 */

export interface TickerItem {
  readonly display: string;
  readonly wonTon: string;
  readonly tier: string;
  readonly agoSeconds: number;
}

export type TickerView =
  | { readonly kind: 'hidden' }
  | { readonly kind: 'shown'; readonly items: readonly TickerItem[] };

export const HIDDEN_TICKER: TickerView = { kind: 'hidden' };

/** A non-negative decimal string as the server emits (`{whole}.{frac}` or a bare integer). */
const TON_STRING = /^\d+(\.\d+)?$/;

function isObj(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null;
}
function isTonString(v: unknown): v is string {
  return typeof v === 'string' && TON_STRING.test(v);
}
function isNonEmptyString(v: unknown): v is string {
  return typeof v === 'string' && v.length > 0;
}

/**
 * Project the /ticker body. Returns `shown` ONLY when contract_version is 1, items is an array, and
 * EVERY item is well-formed (all-or-hidden: a malformed row signals a contract failure, so we hide
 * the module rather than render a partial row). An empty items array is `hidden`.
 */
export function parseTickerBody(body: unknown): TickerView {
  if (!isObj(body)) return HIDDEN_TICKER;
  if (body.contract_version !== 1) return HIDDEN_TICKER;
  const raw = body.items;
  if (!Array.isArray(raw)) return HIDDEN_TICKER;
  if (raw.length === 0) return HIDDEN_TICKER;

  const items: TickerItem[] = [];
  for (const it of raw) {
    if (!isObj(it)) return HIDDEN_TICKER;
    if (!isNonEmptyString(it.display)) return HIDDEN_TICKER;
    if (!isTonString(it.won_ton)) return HIDDEN_TICKER;
    if (!isNonEmptyString(it.tier)) return HIDDEN_TICKER; // any non-empty tier; component styles known ones
    if (typeof it.ago_seconds !== 'number' || !Number.isFinite(it.ago_seconds) || it.ago_seconds < 0) {
      return HIDDEN_TICKER;
    }
    items.push({
      display: it.display,
      wonTon: it.won_ton,
      tier: it.tier,
      agoSeconds: Math.floor(it.ago_seconds),
    });
  }
  return { kind: 'shown', items };
}
